import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StepIndicator } from '../components/StepIndicator';
import { OnboardingData, initialOnboardingData } from '../types/onboarding';
import type { FlowOutcome, OffboardingContext } from '../types/offboarding';
import { UserDetails } from '../components/steps/UserDetails';
import { ReviewCreate } from '../components/steps/ReviewCreate';
import { OffboardingWorkflow } from '../components/offboarding/OffboardingWorkflow';
import { FlowOffboarding } from '../components/offboarding/FlowOffboarding';
import { ModeSwitch } from '../components/ModeSwitch';
import { Logo } from '../components/Logo';
const STEPS = [{
  id: 1,
  title: 'User Details',
  description: 'Enter the core information for the new Salesforce user and verify license availability.'
}, {
  id: 2,
  title: 'Review & Create',
  description: 'Review all configurations before creating the user and associated records.'
}];
interface OnboardingWizardProps {
  mode: 'onboarding' | 'offboarding';
  setMode: (mode: 'onboarding' | 'offboarding') => void;
  offboardingContext: OffboardingContext;
  flowOutcome: FlowOutcome;
}
export function OnboardingWizard({
  mode,
  setMode,
  offboardingContext,
  flowOutcome
}: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<OnboardingData>(initialOnboardingData);
  const [canAdvance, setCanAdvance] = useState(false);
  const updateFormData = (updates: Partial<OnboardingData>) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  };
  const handleNext = () => {
    if (currentStep < STEPS.length - 1 && canAdvance) setCurrentStep(prev => prev + 1);
  };
  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };
  const handleStepClick = (index: number) => {
    if (index < currentStep) setCurrentStep(index);
  };
  const isLastStep = currentStep === STEPS.length - 1;
  const renderStepContent = () => {
    const props = {
      data: formData,
      updateData: updateFormData,
      setCanAdvance
    };
    switch (currentStep) {
      case 0:
        return <UserDetails {...props} data-id="element-549" data-mp="2quskb91mcjqv" />;
      case 1:
        return <ReviewCreate {...props} onBack={handleBack} data-id="element-550" data-mp="2mtp3aj7u4ho2" />;
      default:
        return null;
    }
  };
  return <div className="min-h-screen w-full bg-[#F0F2F5] font-sans text-gray-900 pb-12" data-id="element-551" data-mp="2hv5iim4z9bxa">
      {/* Top Header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6" data-id="element-552" data-mp="2cy59q3gqtv2h">
        <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-553" data-mp="2852n6fadvlvs">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.3,
          delay: 0.1
        }} className="flex flex-wrap items-center gap-5" data-id="element-554" data-mp="236j3klj9im6l">
            <Logo data-id="element-555" data-mp="1u5t8xl5qn1ug" />
            <ModeSwitch mode={mode} setMode={setMode} data-id="element-556" data-mp="1u8ffbhgw7bb3" />
          </motion.div>

          {/* License Counters */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.3,
          delay: 0.15
        }} className="flex items-center gap-3" data-id="element-557" data-mp="1p9m2zsdcdqci">
            <div className="bg-white rounded-xl px-4 py-2.5 shadow-sm border border-gray-100 flex items-center gap-3" data-id="element-558" data-mp="emve6hqriqxg">
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-navy-600 flex-shrink-0" fill="currentColor" aria-hidden="true" data-id="element-559" data-mp="inyt920zkixm">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" data-id="element-560" data-mp="3i5yd24tn4yqz" />
              </svg>
              <div className="flex flex-col items-end leading-none" data-id="element-561" data-mp="2ze5kucmebhkb">
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider" data-id="element-562" data-mp="2zbt4g3wadf9d">Salesforce</span>
                <span className="text-base font-bold text-gray-900" data-id="element-563" data-mp="2kam2o6kbm8c8">
                  14<span className="text-gray-400 font-medium" data-id="element-564" data-mp="2k8hz5pqc3bch">/58</span>
                </span>
              </div>
            </div>
            <div className="bg-white rounded-xl px-4 py-2.5 shadow-sm border border-gray-100 flex items-center gap-3" data-id="element-565" data-mp="2u05kra07ly61">
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-566" data-mp="2p9fdfr883l8p">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" data-id="element-567" data-mp="2y0z7i01x16xc" />
              </svg>
              <div className="flex flex-col items-end leading-none" data-id="element-568" data-mp="3cyzxkdg44ojk">
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider" data-id="element-569" data-mp="3cx711qz7rkpa">Pulse</span>
                <span className="text-base font-bold text-gray-900" data-id="element-570" data-mp="37ycb852twvvo">
                  6<span className="text-gray-400 font-medium" data-id="element-571" data-mp="1ils9d4mn7b6w">/52</span>
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.15
    }} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-572" data-mp="3paaskj7ofqbb">
        {mode === 'offboarding' ? offboardingContext === 'flow' ? <div className="mx-auto max-w-2xl" data-id="element-573" data-mp="3kgy72hyanzms">
              <FlowOffboarding outcome={flowOutcome} data-id="element-574" data-mp="36ihyelgvfz20" />
            </div> : <div className="mx-auto max-w-4xl" data-id="element-575" data-mp="3akd0vtajgihd">
              <OffboardingWorkflow data-id="element-576" data-mp="3am68pbww18qy" />
            </div> : <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col md:flex-row min-h-[600px] overflow-hidden" data-id="element-577" data-mp="2wlvh26nr7q4q">
            <div className="w-full md:w-[240px] border-b md:border-b-0 md:border-r border-gray-100 flex-shrink-0 bg-white" data-id="element-578" data-mp="31m0zqxc1q2ao">
              <StepIndicator steps={STEPS} currentStep={currentStep} onStepClick={handleStepClick} data-id="element-579" data-mp="zsjshurtv3gg" />
            </div>

            <div className="flex-1 flex flex-col bg-white relative" data-id="element-580" data-mp="ybxqbi2v954y">
              <div className="flex-1 p-6 md:p-10 overflow-y-auto" data-id="element-581" data-mp="y91t5vyuekhk">
                <div className="mb-8" data-id="element-582" data-mp="taqixk4my7yt">
                  <h1 className="text-xl font-bold text-gray-900 mb-2" data-id="element-583" data-mp="od8dw4tqocw3">{STEPS[currentStep].title}</h1>
                  <p className="text-sm text-gray-500" data-id="element-584" data-mp="jednohtfzm1y">{STEPS[currentStep].description}</p>
                </div>

                <AnimatePresence mode="wait" data-id="element-585" data-mp="1h2fcf3t712hc">
                  <motion.div key={currentStep} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2,
                ease: 'easeInOut'
              }} data-id="element-586" data-mp="1h14al9jb64sv">
                    {renderStepContent()}
                  </motion.div>
                </AnimatePresence>

                {!isLastStep && <div className="mt-12 pt-6 border-t border-gray-100 flex items-center justify-between" data-id="element-587" data-mp="1v15ekxuvdsck">
                    {currentStep > 0 ? <button onClick={handleBack} className="text-sm font-medium text-gray-500 hover:text-gray-900 hover:underline transition-colors" data-id="element-588" data-mp="23c4d8tkpl5d0">
                        Previous
                      </button> : <div data-id="element-589" data-mp="2h7pwsl86awy1" />}
                    <button onClick={handleNext} disabled={!canAdvance} className={`px-8 py-2.5 rounded-full font-medium text-sm transition-colors duration-150 ease-out shadow-sm ${!canAdvance ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-navy-600 text-white hover:bg-navy-700'}`} data-id="element-590" data-mp="2m84769x8yiz8">
                      Next
                    </button>
                  </div>}
              </div>
            </div>
          </div>}
      </motion.div>
    </div>;
}